import { useEffect, useState } from "react";
import { fetchMarket } from "@/lib/api";
import { TrendingUp, Minus, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import PrimeIndexChart from "@/components/PrimeIndexChart";

export default function MarketInsights() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchMarket().then(setData).catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <section
      id="insights"
      data-testid="market-section"
      className="py-24 lg:py-36 bg-[#f5f0e8] border-t border-[#1c1c1c]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <SectionHeading
              title={<>The numbers behind<br />the <em className="not-italic text-[#3d2b1f]">prime Dubai</em> story.</>}
              subtitle={`Market Pulse · ${data.as_of}`}
            />
          </div>
          <div className="lg:col-span-5 lg:pt-16">
            <p className="text-[#3d2b1f] text-base leading-relaxed">
              A live snapshot of the metrics our private clients use to allocate capital
              into Dubai&apos;s prime and ultra-prime housing — from foreign-buyer inflows to
              branded-residence premia.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-[#1c1c1c]/10">
          {data.metrics.map((m, i) => (
            <div
              key={m.label}
              data-testid={`metric-${i}`}
              className="p-10 border-b border-r border-[#1c1c1c]/10 last:border-r-0 lg:[&:nth-child(3n)]:border-r-0 hover:bg-white/60 transition-colors"
            >
              <div className="underline-label mb-4 text-[#3d2b1f]/70">{m.label}</div>
              <div className="font-display text-5xl lg:text-6xl text-[#1c1c1c] leading-none">
                {m.value}
              </div>
              <div className="mt-5 text-[12px] text-[#3d2b1f] leading-relaxed">{m.context}</div>
            </div>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <span className="underline-label mb-8 inline-block">Neighbourhood Index — AED / sqft</span>
            <div className="space-y-5">
              {data.neighbourhoods.map((n) => (
                <div
                  key={n.name}
                  data-testid={`hood-${n.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="grid grid-cols-12 items-center gap-4 py-5 border-b border-[#1c1c1c]/10 group hover:border-[#c5a059] transition-colors"
                >
                  <div className="col-span-5 lg:col-span-4 font-display text-xl lg:text-2xl text-[#1c1c1c]">
                    {n.name}
                  </div>
                  <div className="col-span-4 lg:col-span-5">
                    <div className="h-[3px] w-full bg-[#1c1c1c]/8 relative overflow-hidden rounded-full">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#c5a059] rounded-full"
                        style={{ width: `${Math.min(100, (n.avg_psf_aed / 9000) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="col-span-2 text-right font-display text-lg lg:text-xl text-[#1c1c1c]">
                    {n.avg_psf_aed.toLocaleString()}
                  </div>
                  <div className="col-span-1 flex justify-end text-[#c5a059]">
                    {n.trend === "up" ? <TrendingUp size={16} /> : <Minus size={16} />}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#3d2b1f]">
              <ArrowUpRight size={14} className="text-[#c5a059]" />
              Indicative only — full report available to private clients.
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-2">
            <PrimeIndexChart />
          </div>
        </div>
      </div>
    </section>
  );
}
